import { jsPDF } from 'jspdf';

const BALANCE_SHEET_GROUPS = {
  assets: [
    'Cash and Bank',
    'Accounts Receivable',
    'Other Current Assets',
    'Fixed Assets',
    'Long-Term Assets'
  ],
  liabilities: [
    'Accounts Payable',
    'Credit Cards',
    'Current Liabilities',
    'Long-Term Liabilities'
  ],
  equity: [
    'Equity',
    'Retained Earnings',
    'Current Year Earnings'
  ]
};

const escapeCsvCell = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;

const formatAmount = (value) => {
  const amount = Number(value || 0);
  return amount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

const formatDate = (value) => {
  if (!value) return '';
  return new Date(`${value}T00:00:00`).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
};

const download = (blob, fileName) => {
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.href = url;
  link.download = fileName;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const getVisibleGroups = (data, section, showZeroBalances) => (
  Object.entries(data?.[section]?.categories || {}).filter(([, category]) => (
    category && (
      showZeroBalances ||
      Number(category.total || 0) !== 0 ||
      (category.accountDetails || []).some(
        (account) => Number(account.amount || 0) !== 0
      )
    )
  )).map(([name, category]) => ({ name, category }))
);

const getBalanceVariance = (data) => Number(
  (
    Number(data?.assets?.total || 0) -
    Number(data?.liabilities?.total || 0) -
    Number(data?.equity?.total || 0)
  ).toFixed(2)
);

const getWarningRow = (data) => {
  const variance = getBalanceVariance(data);

  if (Math.abs(variance) <= 0.009) return [];

  return [[
    `Warning: Balance Sheet is out of balance by $${formatAmount(Math.abs(variance))}`,
    '',
    ''
  ]];
};

const createRows = (data, viewMode, showZeroBalances) => {
  const rows = [];
  const isDetails = viewMode === 'details';

  ['assets', 'liabilities', 'equity'].forEach((section) => {
    rows.push([section.toUpperCase(), '', '']);

    getVisibleGroups(data, section, showZeroBalances).forEach(({ name, category }) => {
      if (isDetails) {
        rows.push(['', name, '']);
        (category.accountDetails || [])
          .filter((account) => showZeroBalances || Number(account.amount || 0) !== 0)
          .forEach((account) => {
            rows.push([
              account.code || '',
              account.name,
              formatAmount(account.amount)
            ]);
          });
      }

      rows.push(['', `Total ${name}`, formatAmount(category.total)]);
    });

    rows.push(['', `Total ${section}`, formatAmount(data[section].total)], ['', '', '']);
  });

  return rows;
};

export const downloadBalanceSheetCsv = ({
  companyName,
  asOfDate,
  basis,
  reportData,
  viewMode,
  showZeroBalances
}) => {
  const rows = [
    ['Assets, Liabilities & Equity', '', ''],
    [companyName || 'Accounting Workspace', '', ''],
    [`As of ${formatDate(asOfDate)}`, '', ''],
    [`Report Type: ${basis === 'cash' ? 'Cash Basis' : 'Accrual Basis'}`, '', ''],
    ['', '', ''],
    ['ACCOUNT CATEGORY', 'ACCOUNT NAME', 'BALANCE'],
    ...getWarningRow(reportData),
    ...createRows(reportData, viewMode, showZeroBalances)
  ];

  const csv = `\uFEFF${rows.map((row) => row.map(escapeCsvCell).join(',')).join('\r\n')}`;
  download(
    new Blob([csv], { type: 'text/csv;charset=utf-8;' }),
    `Assets_Liabilities_Equity_${asOfDate}_${viewMode}.csv`
  );
};

const drawLine = (pdf, label, value, x, y, width, options = {}) => {
  const { bold = false, indent = 0, shade = false, fontSize = 9 } = options;

  if (shade) {
    pdf.setFillColor(245, 248, 250);
    pdf.rect(x, y - 11, width, 18, 'F');
  }

  pdf.setFont('helvetica', bold ? 'bold' : 'normal');
  pdf.setFontSize(fontSize);
  pdf.setTextColor(31, 41, 55);
  pdf.text(label, x + indent + 4, y);
  pdf.text(formatAmount(value), x + width - 4, y, { align: 'right' });

  pdf.setDrawColor(226, 232, 240);
  pdf.setLineWidth(0.35);
  pdf.line(x + indent + 4, y + 5, x + width - 4, y + 5);
};

export const downloadBalanceSheetPdf = ({
  companyName,
  asOfDate,
  basis,
  reportData,
  viewMode,
  showZeroBalances
}) => {
  const PdfConstructor = typeof jsPDF === 'function' ? jsPDF : window.jspdf?.jsPDF;
  if (typeof PdfConstructor !== 'function') {
    throw new Error('PDF export is unavailable. Please refresh and try again.');
  }

  const pdf = new PdfConstructor({
    orientation: 'portrait',
    unit: 'pt',
    format: 'letter'
  });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 44;
  const contentWidth = pageWidth - margin * 2;
  const details = viewMode === 'details';
  let y = margin;

  const addPage = () => {
    pdf.addPage();
    y = margin;
    drawHeader();
  };

  const ensureSpace = (space) => {
    if (y + space > pageHeight - 48) addPage();
  };

  const drawHeader = () => {
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(16);
    pdf.setTextColor(15, 23, 42);
    pdf.text('Statement of Assets and Liabilities', pageWidth / 2, y, { align: 'center' });
    y += 21;

    if (companyName) {
      pdf.setFontSize(12);
      pdf.text(companyName, pageWidth / 2, y, { align: 'center' });
      y += 17;
    }

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8.5);
    pdf.setTextColor(71, 85, 105);
    pdf.text(`Report Type: ${basis === 'cash' ? 'Cash Basis' : 'Accrual Basis'}`, pageWidth / 2, y, { align: 'center' });
    y += 14;
    pdf.text(`As of ${formatDate(asOfDate)}`, pageWidth / 2, y, { align: 'center' });
    y += 18;

    const variance = getBalanceVariance(reportData);
    if (Math.abs(variance) > 0.009) {
      pdf.setTextColor(180, 83, 9);
      pdf.setFontSize(8);
      pdf.text(
        `Warning: Balance Sheet is out of balance by $${formatAmount(Math.abs(variance))}`,
        margin,
        y
      );
      y += 14;
    }

    pdf.setDrawColor(37, 99, 235);
    pdf.setLineWidth(1);
    pdf.line(margin, y, pageWidth - margin, y);
    y += 20;
  };

  drawHeader();

  ['assets', 'liabilities', 'equity'].forEach((section) => {
    ensureSpace(44);
    pdf.setFillColor(226, 232, 240);
    pdf.rect(margin, y - 12, contentWidth, 20, 'F');
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(9);
    pdf.setTextColor(30, 41, 59);
    pdf.text(section.toUpperCase(), margin + 6, y + 1);
    y += 25;

    getVisibleGroups(reportData, section, showZeroBalances).forEach(({ name, category }) => {
      if (!details) {
        ensureSpace(22);
        drawLine(pdf, `Total ${name}`, category.total, margin, y, contentWidth);
        y += 18;
        return;
      }

      ensureSpace(28);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(8.5);
      pdf.setTextColor(71, 85, 105);
      pdf.text(name, margin + 12, y);
      y += 16;

      (category.accountDetails || [])
        .filter((account) => showZeroBalances || Number(account.amount || 0) !== 0)
        .forEach((account) => {
          ensureSpace(22);
          drawLine(
            pdf,
            account.code ? `${account.code} · ${account.name}` : account.name,
            account.amount,
            margin,
            y,
            contentWidth,
            { indent: 20 }
          );
          y += 18;
        });

      ensureSpace(24);
      drawLine(pdf, `Total ${name}`, category.total, margin, y, contentWidth, {
        bold: true,
        shade: true
      });
      y += 23;
    });

    ensureSpace(28);
    drawLine(pdf, `TOTAL ${section.toUpperCase()}`, reportData[section].total, margin, y, contentWidth, {
      bold: true,
      shade: true,
      fontSize: 10
    });
    y += 32;
  });

  const totalPages = pdf.internal.getNumberOfPages();
  for (let page = 1; page <= totalPages; page += 1) {
    pdf.setPage(page);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(7.5);
    pdf.setTextColor(100, 116, 139);
    pdf.text(
      `Generated ${new Date().toLocaleString('en-US')} · Page ${page} of ${totalPages}`,
      pageWidth - margin,
      pageHeight - 24,
      { align: 'right' }
    );
  }

  pdf.save(`Assets_Liabilities_Equity_${asOfDate}_${viewMode}.pdf`);
};